import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const NAVY = "#0b192c";
const GOLD = "#c4a454";
const CREAM = "#fdfcf8";
const BORDER = "#e2e1dd";
const MUTED = "#475569";

const H2 = ({ children }) => (
  <h2 className="font-serif text-2xl sm:text-3xl mt-10 mb-3 leading-tight" style={{ color: NAVY }}>{children}</h2>
);
const P = ({ children }) => (
  <p className="text-sm sm:text-base mt-3 leading-relaxed" style={{ color: MUTED }}>{children}</p>
);
const UL = ({ children }) => (
  <ul className="mt-3 space-y-2 pl-5 list-disc text-sm sm:text-base leading-relaxed" style={{ color: MUTED }}>{children}</ul>
);

export default function Privacy() {
  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="privacy-page">
      <nav className="sticky top-0 z-40 border-b backdrop-blur-md" style={{ borderColor: BORDER, background: "rgba(253,252,248,0.9)" }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/corpzo-icon-sm.png" alt="Corpzo" className="h-9 w-9" />
            <div className="font-serif text-xl leading-none" style={{ color: NAVY }}>
              Corpzo<span style={{ color: GOLD }}>.</span>
            </div>
          </Link>
          <Link to="/" className="text-sm flex items-center gap-2" style={{ color: MUTED }} data-testid="privacy-back">
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="text-[11px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>Legal</div>
        <h1 className="font-serif text-4xl sm:text-6xl mt-4 leading-tight tracking-tight" style={{ color: NAVY }}>
          Privacy Policy
        </h1>
        <p className="text-sm mt-4" style={{ color: MUTED }}>
          Last updated: February 2026 · Aligned with the Digital Personal Data Protection Act, 2023 (DPDP).
        </p>

        <H2>1. Who we are</H2>
        <P>
          Corpzo Ventures Private Limited (&ldquo;Corpzo&rdquo;) is the Data Fiduciary for information processed through
          AIFLedger. Where your AIF or Investment Manager uses the Platform to process investor / LP data, we act
          as a Data Processor on their behalf. Contact: <a href="mailto:reach@aifledger.com" style={{ color: NAVY, fontWeight: 600 }}>reach@aifledger.com</a>.
        </P>

        <H2>2. What we collect</H2>
        <P>We process the following categories of personal data:</P>
        <UL>
          <li><b>Account data</b>: name, email, phone, designation, employer, roles.</li>
          <li><b>KYC data</b> (for LP onboarding): PAN, Aadhaar (masked), passport, address proof, photograph, CKYC number, FATCA/CRS declarations, PEP declaration, UBO, source-of-funds, nominee, bank & demat details.</li>
          <li><b>Financial data</b>: commitments, capital-account balances, NAV, distributions, tax packs.</li>
          <li><b>Usage data</b>: login timestamps, IP address, device / browser, actions taken (audit log).</li>
          <li><b>Lead form data</b> (public landing page): name, email, phone, fund name, category, corpus band, message.</li>
        </UL>

        <H2>3. Why we collect it (Purpose)</H2>
        <UL>
          <li>To provide fund-administration services requested by your fund.</li>
          <li>To comply with SEBI AIF regulations, PMLA / AML, tax and reporting laws.</li>
          <li>To operate maker-checker workflows and audit trails required by law and by your fund&apos;s trustees / auditors.</li>
          <li>To respond to demo / sales enquiries submitted via the landing form.</li>
          <li>To secure the Platform, prevent fraud, and enforce our Terms.</li>
        </UL>

        <H2>4. Legal basis</H2>
        <P>
          We rely on <b>consent</b> (Section 6, DPDP) for lead-form data, on <b>legitimate use for performance of contract</b>
          for account and fund-operation data, and on <b>legal obligation</b> for retention required by SEBI, tax and audit
          regulations.
        </P>

        <H2>5. How we share it</H2>
        <UL>
          <li>With your AIF, Investment Manager, Sponsor, Trustee, Auditor, RTA and Custodian — for their operational and regulatory duties.</li>
          <li>With regulated third parties for PAN verification, CKYC, e-sign, e-mail delivery, and payment gateways.</li>
          <li>With SEBI or other regulators, when required by law or valid regulatory request.</li>
          <li>We do <b>not</b> sell your personal data. We do not use it for marketing outside of the demo enquiry you initiated.</li>
        </UL>

        <H2>6. Where we store it</H2>
        <P>
          Data is stored on encrypted infrastructure in India. Backups are encrypted at rest. Access is role-scoped and
          logged. Cross-tenant reads are prevented by design.
        </P>

        <H2>7. How long we keep it</H2>
        <UL>
          <li>Fund-operation records: retained for as long as your fund uses the Platform, plus statutory retention (typically 8 years for SEBI-related records).</li>
          <li>Lead-form data: retained for 24 months from submission, unless you request earlier deletion.</li>
          <li>Audit logs: 8 years, immutable.</li>
        </UL>

        <H2>8. Your rights (DPDP)</H2>
        <UL>
          <li><b>Right to access</b> a summary of your personal data.</li>
          <li><b>Right to correction</b> of inaccurate data.</li>
          <li><b>Right to erasure</b>, subject to legal retention obligations.</li>
          <li><b>Right to grievance redressal</b> — contact our Grievance Officer (below).</li>
          <li><b>Right to nominate</b> another person to exercise these rights in the event of death or incapacity.</li>
        </UL>

        <H2>9. Security</H2>
        <P>
          We use TLS for data in transit and encryption at rest. Access is protected by password policy, TOTP MFA, session
          timeouts and IP logging. Sensitive actions require maker-checker approval. Every write is captured in an immutable
          audit log.
        </P>

        <H2>10. Cookies & analytics</H2>
        <P>
          We use strictly necessary cookies for authentication and session management. On the public landing page we use
          privacy-respecting product analytics (Google Analytics 4 with anonymised IPs, and PostHog — no cross-site tracking).
          Non-essential cookies are only set after your explicit consent, given via our cookie banner.
        </P>
        <P>
          You can review or change your choice any time —{" "}
          <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new Event("aif-open-cookie-prefs")); }}
             style={{ color: NAVY, fontWeight: 600, textDecoration: "underline" }}
             data-testid="privacy-cookie-prefs-link">
            Open cookie preferences
          </a>.
        </P>

        <H2>11. Children</H2>
        <P>
          The Platform is not directed to children under 18. We do not knowingly collect personal data from minors, other
          than in the context of a legal-guardian LP relationship (e.g., minor beneficiary under a family trust).
        </P>

        <H2>12. Grievance Officer</H2>
        <P>
          <b>Grievance Officer</b>: Compliance Team, Ultiwise Ventures Private Limited, Noida, India.<br />
          Email: <a href="mailto:reach@aifledger.com" style={{ color: NAVY, fontWeight: 600 }}>reach@aifledger.com</a> · Response within 30 days as required by DPDP.
        </P>

        <H2>13. Changes</H2>
        <P>
          We&apos;ll notify material changes in-product or by email at least 15 days before they take effect.
        </P>

        <div className="mt-16 pt-8 border-t text-xs" style={{ borderColor: BORDER, color: MUTED }}>
          © {new Date().getFullYear()} Ultiwise Ventures Private Limited · <Link to="/terms" className="hover:text-[#0b192c]" data-testid="privacy-terms-link">Terms of Service</Link> · <Link to="/" className="hover:text-[#0b192c]">Home</Link>
        </div>
      </main>
    </div>
  );
}
